import React, { useEffect, useState } from 'react';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { cn, getCmdModifierKeySymbol } from '@cotera/client/v0/lib/utils';

/**
 * How long the box waits after a keystroke before narrowing the grid.
 *
 * Every keystroke is a scan of every column, so running per character is a
 * burst of queries whose answers are all thrown away except the last. Long
 * enough to let a word land, short enough that the grid still reads as
 * following the typing rather than lagging it.
 */
export const DATASET_SEARCH_DEBOUNCE_MS = 250;

/**
 * The free-text search over the rows: one term, every column, substring match.
 *
 * Deliberately the blunt instrument. The header filters already do the precise
 * work — this column, this operator, this value — and the query bar below does
 * the arbitrary work. What was missing between them is the thing a reader
 * reaches for first: type a name, see the rows that mention it, without having
 * to decide which column it lives in.
 *
 * Its own draft, pushed up on a debounce, because setting the term is what runs
 * the query. Typed straight through, an eight-character name is eight scans of
 * the dataset and seven wasted answers.
 *
 * The row count beside it is already the count of what matched — the grid's own
 * number answers to the search exactly as it answers to a filter — so there is
 * no separate "n results" here to go stale beside it.
 */
export const DatasetSearch: React.FC<{
  /** The applied term. Setting it is what narrows the grid. */
  value: string;
  onChange: (value: string) => void;
  /** Focus handle, so the page's ⌘F can put the cursor here. */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}> = ({ value, onChange, inputRef }) => {
  const [draft, setDraft] = useState(value);
  // Read through a ref so a caller passing a fresh closure each render does not
  // restart the debounce and postpone the query indefinitely.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Adopts a term set from outside — the one carried over from earlier in the
  // visit, or a clear from the empty state, both of which arrive after this box
  // has already mounted.
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) {
      return;
    }
    const timer = setTimeout(
      () => onChangeRef.current(draft),
      DATASET_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [draft, value]);

  const clear = () => {
    setDraft('');
    onChangeRef.current('');
  };

  return (
    <div className="relative flex shrink-0 items-center">
      <Icon
        icon="magnifying-glass"
        size="small"
        className="pointer-events-none absolute left-2 opacity-60"
      />
      <input
        ref={inputRef}
        // Not `type="search"`: the browser's own clear affordance is a second
        // control doing the same job as the one below, in a different shape,
        // and only in some browsers.
        type="text"
        value={draft}
        aria-label="Search rows"
        placeholder="Search rows"
        title={`Search rows (${getCmdModifierKeySymbol()} + F)`}
        className={cn(
          // Narrower where the pane is: this is the widest single item in the
          // toolbar, and the first place to find room. Keyed to the dataset
          // toolbar's own container (`dataset-bar`) rather than the window,
          // which is wide while an artifact pane is not.
          'h-6 w-44 rounded-full border border-border bg-background py-0 pl-7 pr-6 @5xl/dataset-bar:w-52',
          // normal-case and tracking-normal are held here rather than left to
          // the surroundings: this is the reader's own words, and a toolbar
          // that shouts its labels must not shout them back.
          'text-[11px] normal-case tracking-normal text-foreground',
          'placeholder:text-muted-foreground/70',
          'transition-colors hover:border-ring/60',
          'focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20'
        )}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && draft !== '') {
            // Claimed rather than left to bubble: a grid-level Escape clears
            // the selection, and a reader emptying a search box does not mean
            // that too.
            event.preventDefault();
            event.stopPropagation();
            clear();
            return;
          }
          if (event.key === 'Enter') {
            // Skips the wait. Someone who has finished typing and pressed Enter
            // has said so.
            event.preventDefault();
            onChangeRef.current(draft);
          }
        }}
      />
      {draft !== '' ? (
        <button
          type="button"
          aria-label="Clear search"
          className="absolute right-1 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={clear}
        >
          <Icon icon="x-mark" size="small" />
        </button>
      ) : null}
    </div>
  );
};
